import { ElMessage } from 'element-plus'
import { storeToRefs } from 'pinia'
import { apiGetPayList } from '@/api/gameApi'
import i18n from '@/i18n'
import { useAuthStore } from '@/store/modules/auth'
import { useVoucherStore } from '@/store/modules/voucher'
import { useLanguageStore } from '@/store/modules/language'
import { useDevice } from '@/hooks/useDevice'
import { useGameStore } from '@/store/modules/game'
import { CommonConstants } from '@/common/constants'
import type { ISkuList } from '@/@types/types'

let payListRequest:
  | {
      key: string
      promise: Promise<boolean>
    }
  | null = null

export const usePayHooks = () => {
  const { currentGameConfig } = storeToRefs(useGameStore())
  const { currentCountry } = storeToRefs(useLanguageStore())
  const { isWeixin, filterPayTypeList } = useDevice()
  const { payTypeList, hPayTypeList, currentPayType, currentPayRate } = storeToRefs(useAuthStore())
  const { currentPayTypeId } = storeToRefs(useVoucherStore())

  const resetPayList = () => {
    payTypeList.value = []
    hPayTypeList.value = []
    currentPayType.value = 0
    currentPayTypeId.value = 0
    currentPayRate.value = 0
  }

  const updatePayList = (displayList?: any[], hiddenList?: any[], targetPayId?: number) => {
    payTypeList.value = displayList || []
    hPayTypeList.value = hiddenList || []
    // 在微信环境只展示微信支付
    if (isWeixin.value && CommonConstants.IS_CN) {
      payTypeList.value = payTypeList.value.filter(p => p.pay_type === -81)
      hPayTypeList.value = hPayTypeList.value.filter(p => p.pay_type === -81)
    }
    // 如果不是safari环境把 sub_pay_type === 'applepay的数据过滤'
    payTypeList.value = filterPayTypeList(payTypeList.value)
    hPayTypeList.value = filterPayTypeList(hPayTypeList.value)

    if (!payTypeList.value.length) {
      ElMessage.warning(i18n.global.t('i18n-no-config'))
      resetPayList()
      return false
    }

    let selectedPayType: any = null
    if (CommonConstants.IS_CN) {
      selectedPayType = payTypeList.value[0]
    } else {
      selectedPayType = payTypeList.value.find(p => p.mall_pay_id === targetPayId || p.pay_type === targetPayId)
      if (!selectedPayType) {
        selectedPayType = payTypeList.value[0]
      }
    }
    currentPayType.value = selectedPayType.mall_pay_id
    currentPayTypeId.value = selectedPayType.pay_type
    currentPayRate.value = selectedPayType.pay_rate
    return true
  }

  const getPayList = async (skuList?: ISkuList[]) => {
    const params = {
      mall_game_id: currentGameConfig.value.id,
      pay_region: currentCountry.value.key,
      ...(skuList ? { skuList } : {})
    }
    const requestKey = JSON.stringify(params)
    if (payListRequest?.key === requestKey) {
      return payListRequest.promise
    }

    resetPayList()
    const requestPromise = (async () => {
      try {
        const res: any = await apiGetPayList(params)
        if (res.code !== 0) {
          ElMessage.warning(i18n.global.t('i18n-no-config'))
          return false
        }

        return updatePayList(res.data?.displayList, res.data?.hiddenList)
      } catch (error) {
        ElMessage.warning(i18n.global.t('i18n-no-config'))
        resetPayList()
        return false
      }
    })()

    payListRequest = {
      key: requestKey,
      promise: requestPromise
    }
    return requestPromise.finally(() => {
      if (payListRequest?.key === requestKey) {
        payListRequest = null
      }
    })
  }

  return {
    getPayList,
    resetPayList,
    updatePayList
  }
}
